import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Cross-tenant platform operator role. A superadmin can manage all companies,
 * grant access, and impersonate any tenant. Race-safe.
 */
export function useIsSuperadmin() {
  const { user, isLoading: authLoading } = useAuth();
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refetchTick, setRefetchTick] = useState(0);

  const refetch = useCallback(() => setRefetchTick((n) => n + 1), []);

  useEffect(() => {
    if (authLoading) {
      setIsLoading(true);
      return;
    }
    if (!user) {
      setIsSuperadmin(false);
      setError(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    (async () => {
      const { data, error: rpcError } = await supabase.rpc('has_custom_role', {
        _user_id: user.id,
        _role_name: 'superadmin',
      });
      if (cancelled) return;
      if (rpcError) {
        setError(rpcError as unknown as Error);
        setIsLoading(false);
        return;
      }
      setError(null);
      setIsSuperadmin(data === true);
      setIsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [user, authLoading, refetchTick]);

  return { isSuperadmin, isLoading, error, refetch };
}
